import React, { useState } from 'react';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import DocumentsSection from './DocumentsSection';
import StayHistoryLog from './StayHistoryLog';
import CommunicationHistoryLog from './CommunicationHistoryLog'; // New Import
import { useStore } from '../context/StoreContext';

const { FiX, FiUser, FiMail, FiPhone, FiBriefcase, FiStar, FiHeart, FiShield, FiTag, FiMessageSquare, FiClock, FiLayers } = FiIcons;

const TenantProfileModal = ({ isOpen, onClose, tenant, documents, properties }) => {
  const { data } = useStore();
  const [activeView, setActiveView] = useState('overview'); // 'overview', 'history', 'comms'

  if (!isOpen || !tenant) return null;

  const tenantHistory = (data.stayHistory || []).filter(h => h.tenantId === tenant.id);
  const tenantComms = (data.communications || []).filter(c => c.tenantId === tenant.id);
  
  const lifetimeValue = tenantHistory.reduce((sum, h) => sum + h.totalRent, 0);
  const totalNights = tenantHistory.reduce((sum, h) => {
    const start = new Date(h.startDate);
    const end = new Date(h.endDate);
    return sum + Math.ceil((end - start) / (1000 * 60 * 60 * 24));
  }, 0);

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-md flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-hidden flex flex-col animate-in zoom-in duration-200">
        {/* Profile Header */}
        <div className="bg-indigo-600 p-8 text-white relative shrink-0">
          <button onClick={onClose} className="absolute top-6 right-6 text-white/80 hover:text-white transition-colors">
            <SafeIcon icon={FiX} size={28} />
          </button>
          
          <div className="flex flex-col md:flex-row items-center gap-8">
            <div className="w-28 h-28 bg-white/20 backdrop-blur-xl rounded-3xl flex items-center justify-center text-4xl font-black border border-white/30 shadow-inner">
              {tenant.name.split(' ').map(n => n[0]).join('')}
            </div>
            <div className="text-center md:text-left flex-1">
              <div className="flex items-center justify-center md:justify-start gap-3 mb-2">
                <h2 className="text-3xl font-black tracking-tight">{tenant.name}</h2>
                <div className="flex gap-0.5">
                  {[...Array(5)].map((_, i) => (
                    <SafeIcon key={i} icon={FiStar} size={16} className={i < tenant.rating ? "fill-yellow-400 text-yellow-400" : "text-white/30"} />
                  ))}
                </div>
              </div>
              <div className="flex flex-wrap items-center justify-center md:justify-start gap-x-6 gap-y-2 text-indigo-100 text-sm font-medium">
                <span className="flex items-center gap-1.5"><SafeIcon icon={FiMail} size={14} className="opacity-70" /> {tenant.email}</span>
                <span className="flex items-center gap-1.5"><SafeIcon icon={FiPhone} size={14} className="opacity-70" /> {tenant.phone}</span>
                <span className="flex items-center gap-1.5"><SafeIcon icon={FiBriefcase} size={14} className="opacity-70" /> {tenant.employer}</span>
              </div>
            </div>
            {/* Quick Stats in Header */}
            <div className="hidden lg:flex gap-4">
              <div className="bg-white/10 p-4 rounded-2xl border border-white/10 text-center min-w-[120px]">
                <p className="text-[10px] font-black uppercase opacity-60">Lifetime Value</p>
                <p className="text-xl font-black">${lifetimeValue.toLocaleString()}</p>
              </div>
              <div className="bg-white/10 p-4 rounded-2xl border border-white/10 text-center min-w-[120px]">
                <p className="text-[10px] font-black uppercase opacity-60">Total Nights</p>
                <p className="text-xl font-black">{totalNights}</p>
              </div>
            </div>
          </div>

          {/* View Toggle Tabs */}
          <div className="flex mt-8 gap-4 border-b border-white/10">
            <button 
              onClick={() => setActiveView('overview')}
              className={`pb-3 text-xs font-black uppercase tracking-widest transition-all px-2 flex items-center gap-2 ${activeView === 'overview' ? 'border-b-2 border-white text-white' : 'text-white/50 hover:text-white/80'}`}
            >
              <SafeIcon icon={FiUser} size={12} /> Overview
            </button>
            <button 
              onClick={() => setActiveView('history')}
              className={`pb-3 text-xs font-black uppercase tracking-widest transition-all px-2 flex items-center gap-2 ${activeView === 'history' ? 'border-b-2 border-white text-white' : 'text-white/50 hover:text-white/80'}`}
            >
              <SafeIcon icon={FiClock} size={12} /> Stay Log
            </button>
            <button 
              onClick={() => setActiveView('comms')}
              className={`pb-3 text-xs font-black uppercase tracking-widest transition-all px-2 flex items-center gap-2 ${activeView === 'comms' ? 'border-b-2 border-white text-white' : 'text-white/50 hover:text-white/80'}`}
            >
              <SafeIcon icon={FiMessageSquare} size={12} /> Comms History
            </button>
          </div>
        </div>

        {/* Dynamic Content */}
        <div className="flex-1 overflow-y-auto p-8 bg-gray-50/30">
          {activeView === 'overview' && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2 space-y-8">
                <section>
                  <h3 className="text-xs font-black text-gray-400 uppercase tracking-widest mb-4 flex items-center gap-2">
                    <SafeIcon icon={FiShield} className="text-amber-500" /> Private Operator Intelligence
                  </h3>
                  <div className="p-6 bg-white rounded-3xl border border-gray-100 shadow-sm">
                    <p className="text-sm text-gray-700 leading-relaxed font-medium italic">"{tenant.notes}"</p>
                    <div className="mt-4 pt-4 border-t border-gray-50 grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-[10px] font-bold text-gray-400 uppercase mb-1">Emergency Contact</p>
                        <p className="text-xs font-black text-gray-900">{tenant.emergencyContact}</p>
                      </div>
                      <div>
                        <p className="text-[10px] font-bold text-gray-400 uppercase mb-1">Status</p>
                        <span className="px-2 py-0.5 bg-green-50 text-green-700 text-[10px] font-black rounded uppercase">Verified Guest</span>
                      </div>
                    </div>
                  </div>
                </section>

                <section>
                  <h3 className="text-xs font-black text-gray-400 uppercase tracking-widest mb-4 flex items-center gap-2">
                    <SafeIcon icon={FiTag} className="text-purple-500" /> Categorization
                  </h3>
                  <div className="flex flex-wrap gap-2">
                    {tenant.tags.map((tag, i) => (
                      <span key={i} className="px-4 py-2 bg-purple-50 text-purple-700 text-xs font-black rounded-xl uppercase tracking-tight border border-purple-100">
                        {tag}
                      </span>
                    ))}
                  </div>
                </section>
              </div>

              <div className="space-y-8">
                <DocumentsSection recordType="tenant" recordId={tenant.id} title="Tenant Documents" />
              </div>
            </div>
          )}
          
          {activeView === 'history' && (
            <StayHistoryLog stays={tenantHistory} properties={properties} />
          )}

          {activeView === 'comms' && (
            <CommunicationHistoryLog communications={tenantComms} />
          )}
        </div>

        <div className="p-6 border-t border-gray-100 bg-white flex justify-end gap-3 shrink-0">
          <button onClick={onClose} className="px-6 py-3 font-bold text-gray-400 hover:text-gray-600 transition">
            Dismiss
          </button>
          <button className="px-8 py-3 bg-indigo-600 text-white font-black rounded-2xl hover:bg-indigo-700 transition shadow-lg shadow-indigo-100">
            Edit Profile
          </button>
        </div>
      </div>
    </div>
  );
};

export default TenantProfileModal;